"use client";

import { useState } from "react";
import { ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function FleetCarousel() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const slides = [{
    title: "Executive Sedan",
    description: "Luxury black sedan for discreet, professional business travel.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/executive_sedan_fleet.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/executive_sedan_fleet.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/executive_sedan_fleet.webp-wvc-sizes",
    alt: "Luxury black sedan parked in downtown Los Angeles"
  }, {
    title: "Premium SUV",
    description: "Spacious, high-end SUV perfect for airport transfers and groups.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/premium_suv_fleet.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/premium_suv_fleet.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/premium_suv_fleet.png-wvc-sizes",
    alt: "Black premium SUV ready for VIP transportation"
  }, {
    title: "Airport Service",
    description: "Punctual, reliable chauffeur service to and from LAX.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/lax_airport_transfer.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/lax_airport_transfer.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/lax_airport_transfer.webp-wvc-sizes",
    alt: "Chauffeur waiting at LAX airport terminal"
  }, {
    title: "VIP Chauffeur",
    description: "Professional, discreet drivers for your special events.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/vip_chauffeur_service.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/vip_chauffeur_service.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/vip_chauffeur_service.png-wvc-sizes",
    alt: "Professional chauffeur in suit opening car door"
  }, {
    title: "Hourly Booking",
    description: "Flexible, dedicated transportation for your entire schedule.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/hourly_booking_service.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/hourly_booking_service.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/hourly_booking_service.webp-wvc-sizes",
    alt: "Luxury vehicle waiting for hourly service client"
  }, {
    title: "Corporate Travel",
    description: "Reliable, upscale travel solutions for business executives.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/corporate_travel_fleet.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/corporate_travel_fleet.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/corporate_travel_fleet.png-wvc-sizes",
    alt: "Business executive entering a luxury black car"
  }];
  return <section data-section-id="1584" id="fleetcarousel" className="relative text-white py-24 overflow-hidden !bg-[hsl(24,10%,10%)] md:py-32">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 -right-40 w-[60vw] h-[60vw] rounded-lg opacity-[0.05]" style={{
      background: "radial-gradient(circle at center, #C5A059 0%, transparent 70%)"
    }} />

      <div className="relative max-w-[1440px] mx-auto">
        {}
        <div className="px-6 md:px-12 lg:px-20 mb-12 md:mb-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
            <div className="max-w-2xl">
              <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-4">
                Elite Mobility
              </p>
              <h2 className="font-bold text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight" style={{
              fontFamily: "var(--font-heading)"
            }}>
                Our <span className="text-[#C5A059]">Premium</span> Fleet
              </h2>
              <p className="mt-6 text-lg text-[#A1A1A1] font-sans max-w-xl leading-relaxed">
                Experience the pinnacle of luxury travel in Los Angeles. Our meticulously maintained fleet of black sedans and SUVs ensures every journey is defined by comfort and prestige.
              </p>
              <div className="mt-8">
                <Button asChild className="bg-[#C5A059] hover:bg-[#B8935A] text-black font-bold uppercase tracking-widest px-8 py-6 rounded-md transition-all duration-200 shadow-lg">
                  <Link to="tel:323-492-6292" className="flex items-center gap-3">
                    <Phone className="size-5" />
                    Call Now: 323-492-6292
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="flex overflow-x-auto gap-8 px-6 md:px-12 lg:px-20 pb-8 no-scrollbar">
          {slides.map((slide, i) => <div data-index={i} key={i} onMouseEnter={() => setHoveredIndex(i)} onMouseLeave={() => setHoveredIndex(null)} className="group transition-transform duration-300 hover:scale-105 flex-none w-[85vw] md:w-[45vw] lg:w-[30vw] xl:w-[22vw]">
              <div className="relative h-full flex flex-col bg-black/40 backdrop-blur-md border border-[#333] rounded-lg overflow-hidden shadow-lg transition-all duration-300 hover:border-[#C5A059]/60">
                {}
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={slide.src} data-wvc-srcset={slide.srcset} data-wvc-sizes={slide.sizes} alt={slide.alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110" />
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-300" />
                </div>

                {}
                <div className="p-8 flex flex-col flex-grow !bg-[hsl(0,0%,25%)]">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-[#C5A059] font-display font-bold text-sm tracking-widest">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="h-px w-8 bg-[#C5A059]/40" />
                  </div>
                  
                  <h3 className="font-display font-semibold text-2xl mb-3 group-hover:text-[#C5A059] transition-colors duration-300">
                    {slide.title}
                  </h3>
                  
                  <p className="font-sans text-[#A1A1A1] leading-relaxed mb-8 flex-grow">
                    {slide.description}
                  </p>

                  <Link to="/FleetPage" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#C5A059] text-black text-sm font-bold uppercase tracking-widest rounded-md hover:bg-[#B8935A] transition-all duration-200 group/link">
                    View Details
                    <ArrowRight className="size-4 transition-transform duration-200 group-hover/link:translate-x-1" />
                  </Link>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}